/**
 * contracts/adapter.md §10 — filenames.
 *
 * Pure string logic, so it lives in CORE rather than in the worker: the extension map and
 * the slug rules are shared by the single-file download, the ZIP entry namer and the batch
 * archive namer, and three copies would drift.
 *
 * TWO PRODUCT POSITIONS ENCODED HERE, not oversights to be helpfully corrected:
 *   • `lrc-enhanced` → `.lrc`. Two WriteFormat members, one extension, deliberately:
 *     Enhanced LRC is a .lrc file with per-word <mm:ss.xx> tags, not a separate file type.
 *   • NO PREFIX, NO SUFFIX, NO WATERMARK, EVER. The competitor prefixes free downloads;
 *     matching them costs nothing and not matching them is a stated differentiator.
 *
 * Slugification is FILESYSTEM SAFETY ONLY, not SEO.
 */

import type { WriteFormat } from '../../../contracts/cue-model.ts';

export const EXTENSIONS: Readonly<Record<WriteFormat, string>> = {
  srt: '.srt',
  vtt: '.vtt',
  ass: '.ass',
  ssa: '.ssa',
  smi: '.smi',
  sub: '.sub',
  txt: '.txt',
  pdf: '.pdf',
  lrc: '.lrc',
  'lrc-enhanced': '.lrc', // deliberately the same as `lrc`
  sbv: '.sbv',
  json: '.json',
  docx: '.docx',
  ttml: '.ttml',
  sup: '.sup',
  subidx: '.sub',
  subviewer: '.sub',
  scc: '.scc',
  'ebu-stl': '.stl',
  csv: '.csv',
  xlsx: '.xlsx',
};

export const ZIP_EXTENSION = '.zip';

/** Maximum basename length, before the extension. Filesystem safety, not a product limit. */
export const MAX_BASENAME = 100;

/** Used when slugification would otherwise produce nothing. Never an empty basename. */
export const FALLBACK_BASENAME = 'subtitles';

/** Control characters, as a predicate rather than a regex literal: an escape typed
 *  wrong in a character class is invisible in review and silently matches nothing. */
const isControl = (cp: number): boolean => cp < 0x20 || (cp >= 0x7f && cp <= 0x9f);
const stripControl = (s: string): string =>
  [...s].filter((ch) => !isControl(ch.codePointAt(0) as number)).join('');
const PATH_SEPARATORS = /[/\\]/g;
// Characters Windows refuses in a filename. Kept as a named set rather than a regex literal
// scattered through three call sites.
const WINDOWS_ILLEGAL = /[<>:"|?*]/g;
const WHITESPACE_RUN = /\s+/g;
const WINDOWS_RESERVED = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

/**
 * Filesystem-safe basename. Strips control characters and path separators, collapses
 * whitespace, caps the length and NEVER emits an empty basename.
 *
 * A Windows-reserved basename (`CON`, `NUL`, `COM1`…) is renamed rather than passed through:
 * the name never touches a filesystem HERE, but it reaches `InputRef.name` and then the
 * output ZIP, where it does.
 */
export function slugifyBasename(raw: string, fallback = FALLBACK_BASENAME): string {
  let s = stripControl(raw)

    .replace(PATH_SEPARATORS, ' ')
    .replace(WINDOWS_ILLEGAL, '')
    .replace(WHITESPACE_RUN, ' ')
    .trim();

  // A trailing dot or space is legal in the string and illegal as a Windows filename.
  s = s.replace(/[. ]+$/, '');
  if (s.length > MAX_BASENAME) s = s.slice(0, MAX_BASENAME).replace(/[. ]+$/, '');
  if (s === '') return fallback;
  if (WINDOWS_RESERVED.test(s)) return `${s}_`;
  return s;
}

/** The basename of a path-ish name, with its extension removed. */
export function basenameOf(name: string): string {
  const withoutDirs = name.replace(/^.*[/\\]/, '');
  const dot = withoutDirs.lastIndexOf('.');
  return dot > 0 ? withoutDirs.slice(0, dot) : withoutDirs;
}

/**
 * `{original basename}.{ext}` — the single-file download name.
 * No prefix, no suffix, no watermark.
 */
export function outputName(sourceName: string, format: WriteFormat): string {
  return slugifyBasename(basenameOf(sourceName)) + EXTENSIONS[format];
}

/**
 * `{first basename}-and-{n}-more.zip`, or `subtitles.zip` when ambiguous.
 */
export function archiveName(sourceNames: readonly string[]): string {
  if (sourceNames.length === 0) return FALLBACK_BASENAME + ZIP_EXTENSION;
  const first = slugifyBasename(basenameOf(sourceNames[0] as string), '');
  if (first === '') return FALLBACK_BASENAME + ZIP_EXTENSION;
  if (sourceNames.length === 1) return first + ZIP_EXTENSION;
  return slugifyBasename(`${first}-and-${sourceNames.length - 1}-more`) + ZIP_EXTENSION;
}

/**
 * Resolves collisions with ` (2)`, ` (3)`, … The suffix goes on the BASENAME, before the
 * extension, which is what every desktop file manager does and what a user expects to see.
 *
 * `taken` is mutated: pass the same set for a whole archive so the numbering is stable and
 * a third collision becomes ` (3)` rather than ` (2)` again.
 */
export function uniqueName(desired: string, taken: Set<string>): string {
  const key = desired.toLowerCase();
  if (!taken.has(key)) {
    taken.add(key);
    return desired;
  }
  const dot = desired.lastIndexOf('.');
  const base = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : '';
  for (let n = 2; n < 100_000; n++) {
    const candidate = `${base} (${n})${ext}`;
    if (!taken.has(candidate.toLowerCase())) {
      taken.add(candidate.toLowerCase());
      return candidate;
    }
  }
  /* c8 ignore next */
  throw new RangeError(`uniqueName: could not resolve a collision for ${desired}`);
}

/**
 * ZIP entry names are HOSTILE INPUT. Before a name from an archive is used for anything:
 * reject absolute paths and any `..` segment (zip-slip), strip control characters and path
 * separators, cap the length, rename Windows-reserved basenames.
 *
 * Returns `null` for a name that must be refused outright — the caller skips the entry with
 * a per-file reason rather than failing the whole archive.
 */
export function sanitizeZipEntryName(raw: string): string | null {
  if (raw === '') return null;
  // Directory entries are skipped by the caller, not renamed.
  if (raw.endsWith('/') || raw.endsWith('\\')) return null;
  // Absolute paths, drive letters and UNC paths.
  if (/^[/\\]/.test(raw) || /^[a-zA-Z]:[/\\]/.test(raw)) return null;
  // Any traversal segment, in either separator style.
  const segments = raw.split(/[/\\]/);
  if (segments.some((s) => s === '..')) return null;

  const leaf = segments[segments.length - 1] ?? '';
  const dot = leaf.lastIndexOf('.');
  const base = dot > 0 ? leaf.slice(0, dot) : leaf;
  const ext = dot > 0 ? leaf.slice(dot) : '';
  const safeBase = slugifyBasename(base, '');
  if (safeBase === '') return null;
  const safeExt = stripControl(ext).replace(PATH_SEPARATORS, '').replace(WINDOWS_ILLEGAL, '');
  return safeBase + safeExt;
}
